(() => {
  'use strict';

  const NOTICE_KEY = 'maxler:localization-content-fallback';
  const normalizeLocale = value => String(value || '').toLowerCase();
  const normalizeCountry = value => String(value || '').toUpperCase();

  class MaxlerLocalization extends HTMLElement {
    connectedCallback() {
      this.disconnectController();
      this.controller = new AbortController();
      this.fetchController = null;
      this.positionFrame = 0;
      this.isOpen = false;
      this.form = this.querySelector('form');
      this.trigger = this.querySelector('[data-localization-trigger]');
      this.panel = this.querySelector('[data-localization-panel]');
      this.countrySelect = this.querySelector('[data-localization-country]');
      this.languageSelect = this.querySelector('[data-localization-language]');
      this.saveButton = this.querySelector('[data-localization-save]');
      this.status = this.querySelector('[data-localization-message]');
      this.error = this.querySelector('[data-localization-error]');
      this.closeButton = this.querySelector('[data-localization-close]');
      this.appliedCountry = normalizeCountry(this.dataset.country);
      this.appliedLanguage = normalizeLocale(this.dataset.language);
      this.strings = this.readStrings();

      if (!this.form || !this.trigger || !this.panel || !this.countrySelect || !this.languageSelect) return;

      const signal = this.controller.signal;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', this.panel.id);
      this.panel.setAttribute('popover', 'manual');

      this.trigger.addEventListener('click', event => {
        event.preventDefault();
        this.isOpen ? this.close(true) : this.open();
      }, { signal });
      this.closeButton?.addEventListener('click', event => {
        event.preventDefault();
        this.close(true);
      }, { signal });
      this.countrySelect.addEventListener('change', () => this.updateLanguages(true), { signal });
      this.form.addEventListener('submit', event => this.submit(event), { signal });
      document.addEventListener('pointerdown', event => {
        if (this.isOpen && !event.composedPath().includes(this)) this.close(false);
      }, { signal, capture: true });
      document.addEventListener('keydown', event => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.close(true);
          return;
        }
        if (event.key === 'Tab') requestAnimationFrame(() => {
          if (this.isOpen && !this.contains(document.activeElement)) this.close(false);
        });
      }, { signal, capture: true });

      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;
        this.fetchController?.abort();
        this.fetchController = null;
        if (this.saveButton) { this.saveButton.disabled = false; this.saveButton.textContent = this.strings.save; }
      }, { signal });
      requestAnimationFrame(() => { if (this.isConnected && this.triggerVisible()) this.restoreFallbackNotice(); });
    }

    disconnectedCallback() {
      this.disconnectController();
    }

    disconnectController() {
      if (this.panel?.matches(':popover-open')) {
        try { this.panel.hidePopover(); } catch (_) {}
      }
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      this.openController?.abort();
      if (this.controller) this.controller.abort();
      if (this.fetchController) this.fetchController.abort();
      if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
      this.controller = null;
      this.fetchController = null;
      this.positionFrame = 0;
      this.isOpen = false;
    }

    readStrings() {
      try {
        return JSON.parse(this.dataset.strings || '{}');
      } catch (_) {
        return {};
      }
    }

    clearMessages() {
      if (this.status) {
        this.status.textContent = '';
        this.status.hidden = true;
      }
      if (this.error) {
        this.error.textContent = '';
        this.error.hidden = true;
      }
    }

    showStatus(message) {
      if (this.status) {
        this.status.textContent = message || '';
        this.status.hidden = !message;
      }
    }

    showError(message) {
      if (this.error) {
        this.error.textContent = message || '';
        this.error.hidden = !message;
      }
    }

    resetPending() {
      this.clearMessages();
      this.countrySelect.value = this.appliedCountry;
      this.updateLanguages(false, this.appliedLanguage);
    }

    currentLanguages() {
      const option = this.countrySelect.selectedOptions[0];
      try {
        const rows = JSON.parse(option?.dataset.languages || '[]');
        return Array.isArray(rows) ? rows.filter(row => row && row.code) : [];
      } catch (_) {
        return [];
      }
    }

    updateLanguages(announceFallback, preferred = this.languageSelect.value) {
      this.clearMessages();
      const languages = this.currentLanguages();
      const select = this.languageSelect;
      const previous = normalizeLocale(preferred);
      const match = languages.find(row => normalizeLocale(row.code) === previous);
      const english = languages.find(row => normalizeLocale(row.code) === 'en');
      const fallback = match || english || languages[0];
      select.replaceChildren();

      languages.forEach(row => {
        const option = document.createElement('option');
        option.value = row.code;
        option.textContent = row.name || row.code;
        select.append(option);
      });

      if (fallback) select.value = fallback.code;
      select.disabled = languages.length <= 1;
      if (languages.length === 1 && normalizeLocale(fallback?.code) === 'en') this.showStatus(this.strings.englishOnly);
      if (announceFallback && !match && fallback) {
        if (normalizeLocale(fallback.code) === 'en') {
          this.showStatus(this.strings.fallback || '');
        } else if (this.strings.fallbackOther) {
          this.showStatus(String(this.strings.fallbackOther).replace('__LANGUAGE__', fallback.name || fallback.code));
        }
      }
    }

    open() {
      if (!this.panel || this.isOpen) return;
      document.querySelectorAll('maxler-localization').forEach(component => {
        if (component !== this && typeof component.close === 'function') component.close(false);
      });
      this.resetPending();
      try {
        if (!this.panel.matches(':popover-open')) this.panel.showPopover();
      } catch (_) {
        return;
      }
      this.isOpen = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.schedulePosition();
      this.openController?.abort();
      this.openController = new AbortController();
      const signal = this.openController.signal;
      window.addEventListener('resize', this.onViewportChange = () => this.schedulePosition(), { signal, passive: true });
      window.addEventListener('scroll', this.onViewportChange, { signal, passive: true, capture: true });
      this.countrySelect.focus({ preventScroll: true });
    }

    close(returnFocus) {
      if (!this.panel || !this.isOpen) return;
      this.isOpen = false;
      this.openController?.abort();
      if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
      this.positionFrame = 0;
      try {
        if (this.panel.matches(':popover-open')) this.panel.hidePopover();
      } catch (_) {}
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.style.removeProperty('top');
      this.panel.style.removeProperty('left');
      if (returnFocus && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }

    triggerVisible() {
      const rect = this.trigger.getBoundingClientRect();
      const style = getComputedStyle(this.trigger);
      return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' &&
        rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    }

    schedulePosition() {
      if (!this.isOpen || this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = 0;
        if (!this.isOpen) return;
        if (!this.triggerVisible()) {
          this.close(false);
          return;
        }
        const triggerRect = this.trigger.getBoundingClientRect();
        const margin = 12;
        const width = Math.max(0, Math.min(380, window.innerWidth - margin * 2));
        this.panel.style.position = 'fixed';
        this.panel.style.width = `${width}px`;
        const panelRect = this.panel.getBoundingClientRect();
        const left = Math.max(margin, Math.min(window.innerWidth - width - margin, triggerRect.right - width));
        const top = Math.max(margin, Math.min(window.innerHeight - panelRect.height - margin, triggerRect.bottom + 8));
        this.panel.style.left = `${left}px`;
        this.panel.style.top = `${top}px`;
      });
    }

    safeReturnTo(targetPath, targetDiffers) {
      let target;
      try {
        target = new URL(targetPath || `${location.pathname}${location.search}`, location.origin);
      } catch (_) {
        target = new URL(location.href);
      }
      if (target.origin !== location.origin) target = new URL(`${location.pathname}${location.search}`, location.origin);
      if (targetDiffers && this.dataset.pageType === 'product') {
        target.searchParams.delete('variant');
        target.searchParams.delete('selling_plan');
      }
      const previewRole = window.Shopify?.theme?.role;
      if (['unpublished', 'development'].includes(String(previewRole || '').toLowerCase()) && window.Shopify?.theme?.id) {
        target.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
      }
      return `${target.pathname}${target.search}`;
    }

    destinationFor(country, language) {
      let pathname = `${location.pathname}${location.search}`;
      if (this.dataset.pageType === 'article') {
        const article = document.querySelector('[data-maxler-article-localization]');
        const key = normalizeCountry(country) === 'US' ? 'urlUs' : normalizeLocale(language) === 'de' ? 'urlDe' : 'urlEn';
        const candidate = article?.dataset[key];
        if (candidate) {
          try {
            const url = new URL(candidate, location.origin);
            if (url.origin === location.origin) pathname = `${url.pathname}${url.search}`;
          } catch (_) {}
        }
      }
      const route = new URL(pathname, location.origin);
      for (const [key, value] of new URLSearchParams(location.search)) {
        if (/^(utm_|gclid$|dclid$|fbclid$|msclkid$|preview_theme_id$|preview_key$|pb$)/.test(key)) route.searchParams.set(key, value);
      }
      pathname = route.pathname + route.search;
      const targetDiffers = route.pathname !== location.pathname;
      return this.safeReturnTo(pathname, targetDiffers);
    }

    async submit(event) {
      event.preventDefault();
      if (this.saveButton?.disabled || !this.form) return;
      this.clearMessages();
      const country = normalizeCountry(this.countrySelect.value);
      const language = normalizeLocale(this.languageSelect.value);
      if (!country || !language) {
        this.showError(this.strings.error || '');
        return;
      }
      if (country === this.appliedCountry && language === this.appliedLanguage) {
        this.close(true);
        return;
      }

      const formData = new FormData(this.form);
      formData.set('country_code', country);
      formData.set('language_code', language);
      formData.set('return_to', this.destinationFor(country, language));
      const action = new URL(this.form.action || `${this.dataset.root || '/'}localization`, location.href);
      if (action.origin !== location.origin) {
        this.showError(this.strings.error || '');
        return;
      }

      const requestController = new AbortController();
      this.fetchController = requestController;
      if (this.saveButton) {
        this.saveButton.disabled = true;
        this.saveButton.dataset.originalText ||= this.saveButton.textContent;
        this.saveButton.textContent = this.strings.saving || this.saveButton.dataset.originalText;
      }
      try {
        const response = await fetch(action.href, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          redirect: 'follow',
          headers: { Accept: 'text/html' },
          signal: requestController.signal
        });
        if (response.url && new URL(response.url, location.href).origin !== location.origin) throw new Error('Unexpected localization destination');
        const html = await response.text();
        const page = new DOMParser().parseFromString(html, 'text/html');
        const applied = page.querySelector('maxler-localization');
        const matches = applied && normalizeCountry(applied.dataset.country) === country && normalizeLocale(applied.dataset.language) === language;

        if (response.status === 404 && matches) {
          const root = new URL(applied.dataset.root || this.dataset.root || '/', response.url || location.href);
          if (root.origin !== location.origin) throw new Error('Unexpected fallback destination');
          root.search = location.search;
          root.searchParams.delete('variant');
          root.searchParams.delete('selling_plan');
          const previewRole = window.Shopify?.theme?.role;
          if (['unpublished', 'development'].includes(String(previewRole || '').toLowerCase()) && window.Shopify?.theme?.id) {
            root.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
          }
          try {
            sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ country, language, message: this.strings.contentFallback || '' }));
          } catch (_) {}
          location.assign(`${root.pathname}${root.search}`);
          return;
        }

        if (!response.ok || !matches) throw new Error('Localization was not applied');
        const destination = new URL(response.url || location.href, location.href);
        if (destination.origin !== location.origin) throw new Error('Unexpected localization destination');
        const requested = new URL(formData.get('return_to'), location.origin);
        for (const [key, value] of requested.searchParams) {
          if (/^(utm_|gclid$|dclid$|fbclid$|msclkid$|preview_theme_id$|preview_key$|pb$|variant$|selling_plan$)/.test(key)) destination.searchParams.set(key, value);
        }
        location.assign(`${destination.pathname}${destination.search}${destination.hash}`);
      } catch (error) {
        if (error?.name !== 'AbortError') this.showError(this.strings.error || '');
      } finally {
        if (this.fetchController !== requestController) return;
        this.fetchController = null;
        if (this.isConnected && this.saveButton) {
          this.saveButton.disabled = false;
          this.saveButton.textContent = this.saveButton.dataset.originalText || this.strings.save || this.saveButton.textContent;
        }
      }
    }

    restoreFallbackNotice() {
      let notice;
      try {
        notice = JSON.parse(sessionStorage.getItem(NOTICE_KEY) || 'null');
        sessionStorage.removeItem(NOTICE_KEY);
        if (notice && normalizeCountry(notice.country) === this.appliedCountry && normalizeLocale(notice.language) === this.appliedLanguage) {
          sessionStorage.removeItem(NOTICE_KEY);
        } else {
          notice = null;
        }
      } catch (_) {
        notice = null;
      }
      if (!notice) return;
      this.resetPending();
      this.open();
      this.showStatus(notice.message || this.strings.contentFallback || '');
    }
  }

  if (!customElements.get('maxler-localization')) customElements.define('maxler-localization', MaxlerLocalization);
})();
